import { defineComponent, ref, onMounted, h } from 'vue';
import { t, setLocale, locales } from '@/ui/i18n/i18n';
import { box, loader, apiErrorMessage, loadingButton } from '@/ui/components';
import { useTask, useValidation } from '@/ui/concerns';
import { getProfile, getTimezones, updateProfile } from '@/services/api/auth';
import { ProfileUpdatingUser, validate as validateProfile } from '@/entities/auth/profileUpdatingUser';
import { DropdownField, dropdownField, inputField } from '@/ui/components/form';
import { supportedLocalesOptions, supportedFormLocalesOptions } from '@/ui/i18n/options';
import { toasted } from '@/ui/helpers';
import { SelectOptions } from '@/ui/types';
import { isFormTranslationsFeatureEnabled } from '@/ui/auth/auth';

export const General = defineComponent({
  name: 'General',
  setup() {
    const hasUpdatedProfile = () => {
      return window.location.href.indexOf('updated') > -1;
    };

    onMounted(() => {
      if (hasUpdatedProfile()) {
        toasted.success(t('profile.updated'));
      }
    });

    const profile = ref<ProfileUpdatingUser>({
      name: '',
      email: '',
      notificationEmail: '',
      phoneNumber: '',
      locale: 'en',
      timezoneId: '1',
      timezone: 'UTC',
      formTranslationsLocale: null,
    });

    const timezones = ref<SelectOptions<string>>([{
      value: '',
      label: '',
    }]);

    const getProfileTask = useTask(
      () => getProfile(),
      loggedInUser => {
        profile.value = loggedInUser;
      },
    );

    useTask(
      () => getTimezones(),
      timezonesLoaded => {
        timezones.value = timezonesLoaded;
      },
    ).run();

    const {
      run: updateProfileTask,
      state: updateProfileState,
    } = useTask(
      () => updateProfile(profile.value),
      () => {
        profileShowValidationErrors.value = false;
        setLocale(profile.value.locale);
        if (hasUpdatedProfile()) {
          window.location.reload();
        } else {
          window.location.search += '&updated=1';
        }
      },
    );

    const {
      validationErrors: profileValidationErrors,
      hasNoValidationErrors: profileHasNoValidationErrors,
      showValidationErrors: profileShowValidationErrors,
    } = useValidation(validateProfile, profile, updateProfileState);

    const handleProfileUpdate = () => {
      profileShowValidationErrors.value = true;
      if (profileHasNoValidationErrors.value) {
        updateProfileTask();
      }
    };

    return function render() {
      return box([loader('form', getProfileTask, [
        inputField({
          name: 'name',
          type: 'test',
          label: t('common.name'),
          placeholder: t('profile.enter-name'),
          value: profile.value.name,
          required: true,
          errors: profileValidationErrors.value,
          disabled: updateProfileState.value.pending,
        }, v => {
          profile.value.name = v;
        }),

        inputField({
          name: 'email',
          type: 'email',
          label: t('common.email'),
          placeholder: t('profile.enter-email'),
          value: profile.value.email,
          required: true,
          errors: profileValidationErrors.value,
          disabled: updateProfileState.value.pending,
        }, v => {
          profile.value.email = v;
        }),

        dropdownField<locales>({
          name: 'locale',
          label: t('common.language'),
          value: profile.value.locale,
          placeholder: '',
          required: true,
          options: supportedLocalesOptions(),
          errors: profileValidationErrors.value,
          disabled: updateProfileState.value.pending,
        }, value => {
          profile.value.locale = value;
        }),

        isFormTranslationsFeatureEnabled.value
          && h(DropdownField, {
            props: {
              name: 'formTranslationsLocale',
              label: t('profile.form-translations-language'),
              tooltipText: t('profile.form-translations-tip'),
              value: profile.value.formTranslationsLocale,
              placeholder: t('common.language'),
              required: false,
              options: supportedFormLocalesOptions(),
              errors: profileValidationErrors.value,
              disabled: updateProfileState.value.pending,
            },
            on: {
              input: (value: locales | null) => {
                profile.value.formTranslationsLocale = value;
              },
            },
          }),

        dropdownField<string>({
          name: 'timezone',
          label: t('common.timezone'),
          value: profile.value.timezoneId,
          placeholder: '',
          required: true,
          options: timezones.value,
          errors: profileValidationErrors.value,
          disabled: updateProfileState.value.pending,
        }, value => {
          profile.value.timezoneId = value;
        }),

        apiErrorMessage(updateProfileState.value),

        loadingButton(
          t('profile.update'),
          handleProfileUpdate,
          updateProfileState.value.pending,
        ),
      ])]);
    };
  },
});
